import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import { useScrollAnimation, getStaggerProps, animationClasses } from '@/hooks/useScrollAnimation';

const identityItems = [
  { label: '正式公司名稱', value: '財經錢線文化有限公司' },
  { label: '對外簡稱', value: '財經錢線' },
  { label: '英文名稱', value: 'FINELINE' },
  { label: '官方網站', value: 'https://finline.sonbook.net/' },
  { label: '主要出版方向', value: '商業理財、心理學與以古諷今類書籍' },
];

const publishingDirections = [
  {
    icon: 'ri-funds-line',
    title: '商業理財',
    description:
      '出版與商業思考、管理、職場及理財知識相關的閱讀內容。此類內容為出版與一般知識資訊，不能取代投資、財務、稅務或法律專業意見。',
  },
  {
    icon: 'ri-psychotherapy-line',
    title: '心理學',
    description:
      '出版與自我理解、情緒、人際互動及大眾心理學相關的閱讀內容。此類內容為出版與一般知識資訊，不能取代合格心理師、精神科醫師或其他專業人員的評估與建議。',
  },
  {
    icon: 'ri-history-line',
    title: '以古諷今',
    description:
      '透過歷史事件、人物與思想，理解當代社會與人文議題。個別作品內容與作者觀點，不代表財經錢線或崧博出版的官方立場。',
  },
];

const contentBoundaries = [
  {
    icon: 'ri-shield-check-line',
    title: '商業理財內容責任',
    description:
      '網站所呈現內容為出版與一般知識資訊，不能取代投資、財務、稅務或法律專業意見。書籍內容不保證獲利、不預測市場走勢，也不提供個人化投資策略。',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: '心理學內容責任',
    description:
      '網站所呈現的心理學內容為出版與一般知識資訊，不能取代合格心理師、精神科醫師或其他專業人員的評估與建議。書籍內容不保證診斷、治療或改善任何心理狀況。',
  },
  {
    icon: 'ri-ancient-pavilion-line',
    title: '以古諷今內容邊界',
    description:
      '「以古諷今」表示透過歷史事件、人物與思想內容，連結或理解當代議題。個別作品內容與作者觀點，不代表財經錢線或崧博出版的官方立場。',
  },
];

export default function About() {
  const heroRef = useScrollAnimation({ threshold: 0.12 });
  const identityRef = useScrollAnimation();
  const directionRef = useScrollAnimation();
  const boundaryRef = useScrollAnimation();
  const relationshipRef = useScrollAnimation();

  return (
    <>
      <PageMeta
        title="關於財經錢線｜FINELINE"
        description="財經錢線（FINELINE）以商業理財、心理學與以古諷今類書籍為主要出版方向。本品牌委託崧博出版經銷，官方網站由崧博出版製作。"
        canonical="/about"
      />
      <Navbar />
      <main>
        {/* ── Hero ── */}
        <section ref={heroRef.ref} className="relative min-h-[100svh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <video
              src="https://public.readdy.ai/ai/video_res/019fea62-6195-75e2-a68f-fde64769cce6.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-900/55 via-primary-800/35 to-primary-900/65"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-24 md:py-32">
            <div className={`max-w-4xl mx-auto text-center ${animationClasses.base} ${heroRef.isVisible ? animationClasses.visible : animationClasses.hidden}`}>
              <div className="flex items-center justify-center mb-6">
                <div className="bg-background-50 rounded-xl px-6 py-3">
                  <img
                    src="https://storage.readdy-site.link/project_files/32ab0c7e-b33a-4beb-bc8e-ca717adf854f/e0f500d6-6fff-40ba-a9db-d68c402b3316_compressed_logo-03.webp"
                    alt="財經錢線 Fineline"
                    className="h-12 w-auto"
                  />
                </div>
              </div>
              <span className="inline-block text-sm md:text-base font-semibold text-accent-200 tracking-wider uppercase mb-4" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)' }}>
                關於品牌
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-5 tracking-tight" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5), 0 4px 24px rgba(0,0,0,0.35)' }}>
                關於財經錢線
              </h1>
              <p className="text-background-50 text-base md:text-lg max-w-2xl mx-auto" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.55), 0 1px 4px rgba(0,0,0,0.35)' }}>
                財經錢線關注商業理財、心理學及以古諷今類出版內容，透過書籍整理商業思維、社會觀察、心理理解與人文議題。
              </p>
            </div>
          </div>
        </section>

        {/* ── Official Identity ── */}
        <section ref={identityRef.ref} className="min-h-[100svh] flex items-center bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className={`text-center mb-10 md:mb-12 ${animationClasses.base} ${identityRef.isVisible ? animationClasses.visible : animationClasses.hidden}`}>
                <span className="inline-block text-xs font-medium text-accent-600 tracking-wider uppercase mb-3">
                  正式資訊
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 tracking-tight">
                  品牌正式資訊
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                  財經錢線以商業理財、心理學與以古諷今類書籍為主要出版方向
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10 md:mb-12">
                {identityItems.slice(0, 3).map((item, i) => (
                  <div
                    key={item.label}
                    {...getStaggerProps(identityRef.isVisible, i, 100)}
                    className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <p className="text-xs font-medium text-foreground-400 tracking-wider uppercase mb-2">
                      {item.label}
                    </p>
                    <p className="text-base md:text-lg font-semibold text-foreground-900">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 md:mb-12">
                {identityItems.slice(3).map((item, i) => (
                  <div
                    key={item.label}
                    {...getStaggerProps(identityRef.isVisible, i + 3, 180)}
                    className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <p className="text-xs font-medium text-foreground-400 tracking-wider uppercase mb-2">
                      {item.label}
                    </p>
                    <p className="text-base font-semibold text-foreground-900">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className={`max-w-3xl mx-auto text-center ${animationClasses.base} ${identityRef.isVisible ? animationClasses.visible : animationClasses.hidden}`}>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                  財經錢線以商業理財、心理學與以古諷今類書籍為主要出版方向，從不同知識主題協助讀者理解商業思維、人性心理及歷史與當代社會之間的關聯。
                </p>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                  財經錢線是出版品牌，不是投資顧問、金融服務、心理諮商、政治評論或選舉服務機構。網站提供正式品牌介紹、出版書籍與相關出版資訊。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Publishing Directions ── */}
        <section ref={directionRef.ref} className="min-h-[100svh] flex items-center bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className={`text-center mb-10 md:mb-14 ${animationClasses.base} ${directionRef.isVisible ? animationClasses.visible : animationClasses.hidden}`}>
                <span className="inline-block text-xs font-medium text-accent-600 tracking-wider uppercase mb-3">
                  出版方向
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 tracking-tight">
                  主要出版方向
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                  財經錢線聚焦商業理財、心理學與以古諷今類書籍，透過不同知識視角，整理個人、社會與時代議題，提供具思考價值的出版內容。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {publishingDirections.map((dir, i) => (
                  <div
                    key={dir.title}
                    {...getStaggerProps(directionRef.isVisible, i, 200)}
                    className="p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-300/40 transition-colors"
                  >
                    <div className="w-10 h-10 flex items-center justify-center mb-4 rounded-lg bg-primary-50">
                      <i className={`${dir.icon} text-primary-500 text-lg`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                      {dir.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {dir.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Content Responsibility ── */}
        <section ref={boundaryRef.ref} className="min-h-[100svh] flex items-center bg-primary-950 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className={`text-center mb-10 md:mb-14 ${animationClasses.base} ${boundaryRef.isVisible ? animationClasses.visible : animationClasses.hidden}`}>
                <span className="inline-block text-xs font-medium text-accent-400 tracking-wider uppercase mb-3">
                  內容責任
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4 tracking-tight">
                  內容責任與使用邊界
                </h2>
                <p className="text-background-50/55 text-sm md:text-base max-w-2xl mx-auto">
                  出版內容為一般知識閱讀，不代表網站提供投資、心理諮商、政治顧問或選舉服務
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {contentBoundaries.map((item, i) => (
                  <div
                    key={item.title}
                    {...getStaggerProps(boundaryRef.isVisible, i, 200)}
                    className="p-6 rounded-xl bg-primary-900/50 border border-primary-800/30"
                  >
                    <div className="w-10 h-10 flex items-center justify-center mb-4">
                      <i className={`${item.icon} text-accent-400 text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-background-50 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-background-50/55 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>

              <div className={`max-w-3xl mx-auto text-center mt-10 md:mt-12 ${animationClasses.base} ${boundaryRef.isVisible ? animationClasses.visible : animationClasses.hidden}`}>
                <p className="text-background-50/45 text-sm leading-relaxed">
                  個別作品內容與作者觀點，不代表財經錢線或崧博出版的官方立場。完整書名、作者、ISBN、出版品牌及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Brand Relationship ── */}
        <section ref={relationshipRef.ref} className="min-h-[100svh] flex items-center bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className={`text-center mb-10 md:mb-12 ${animationClasses.base} ${relationshipRef.isVisible ? animationClasses.visible : animationClasses.hidden}`}>
                <span className="inline-block text-xs font-medium text-accent-600 tracking-wider uppercase mb-3">
                  品牌關係
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 tracking-tight">
                  財經錢線與崧博出版
                </h2>
              </div>

              <div className="max-w-3xl mx-auto text-center mb-10 md:mb-12">
                <p {...getStaggerProps(relationshipRef.isVisible, 0, 0, 'fadeUp')} className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
                  本品牌委託崧博出版經銷，官方網站由崧博出版製作。
                </p>
                <p {...getStaggerProps(relationshipRef.isVisible, 1, 80, 'fadeUp')} className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                  完整書名、作者、ISBN、出版品牌及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。如需查詢完整書目與出版資料，請前往崧博書籍資料庫。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto">
                <a
                  {...getStaggerProps(relationshipRef.isVisible, 2, 160, 'fadeUp')}
                  href="https://sonbook.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看崧博出版與經銷品牌
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line text-sm"></i>
                  </span>
                </a>
                <a
                  {...getStaggerProps(relationshipRef.isVisible, 3, 220, 'fadeUp')}
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold bg-accent-500 text-primary-950 rounded-full hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  前往書籍資料庫
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line text-sm"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}